import {tokens} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import InlineWistiaVideo from '@/components/inline-wistia-video'
import MediaList from '@/components/media-list'
import {ProofPointBadges} from '@/components/proof-point-badges'
import SEO from '@/components/seo'
import SimpleSignupForm from '@/components/simple-signup-form'
import {TypeformSurvey} from '@/components/typeform-survey'
import type {WhyPleoQuery} from '@/graphql-types'
import {BigListSection} from '@/sections/big-list'
import {FeaturesComparisonTable} from '@/sections/features-comparison-table'
import SignupBannerSection from '@/sections/signup-banner'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Button from '@helios/shared/components/button'
import PrismicLink from '@helios/shared/components/prismic-link'
import RichText from '@helios/shared/components/rich-text'
import {Main} from '@helios/shared/components/skip-to-content'
import {Section, Layout, BackgroundWrapper, TitleLayout} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import getEnvironmentFormId from '@helios/shared/utils/get-environment-field'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<WhyPleoQuery>

const WhyPleo = (props: Props) => {
    const data = props.data.prismicWhyPleoPage.data

    if (!data) {
        return handleNoPrismicData()
    }

    const formId = getEnvironmentFormId(
        data.signup_banner.document.data.hubspot_form_id_production,
        data.signup_banner.document.data.hubspot_form_id_staging
    )

    return (
        <>
            <Header />

            <TypeformSurvey />

            <Main>
                <BackgroundWrapper color="muted">
                    <Section padding="small">
                        <FeatureBlock
                            image={data.hero[0].image}
                            title={data.hero[0].title}
                            body={data.hero[0].body.richText}
                            titleVariant="heading-base"
                        >
                            <SimpleSignupForm
                                {...data.signup_banner.document.data}
                                formId={formId}
                                placeholder={
                                    data.signup_banner.document.data.email_input_placeholder
                                }
                                submitText={data.signup_banner.document.data.submit_text}
                                errorText={data.signup_banner.document.data.email_input_error_text}
                            />

                            {!!data.hero[0].proof_point_badges?.document && (
                                <ProofPointBadges
                                    proofPoints={[
                                        data.hero[0].proof_point_badges.document.data
                                            .proof_point_first[0],
                                        data.hero[0].proof_point_badges.document.data
                                            .proof_point_second[0],
                                    ]}
                                />
                            )}
                        </FeatureBlock>
                    </Section>
                </BackgroundWrapper>

                {hasNonEmptyItems(data.how_to_steps) && (
                    <Layout as={Section} variant="two-asymmetrical">
                        <HowToHeader>
                            <Text variant="heading-large">{data.how_to_title}</Text>
                            <RichText content={data.how_to_subtitle.richText} />
                        </HowToHeader>

                        <MediaList items={data.how_to_steps} />
                    </Layout>
                )}

                {hasNonEmptyItems(data.what_is_pleo) && (
                    <Section>
                        <VideoTitle>
                            <Text variant="heading-large">{data.what_is_pleo[0].title}</Text>
                        </VideoTitle>

                        <InlineWistiaVideo
                            videoId={data.what_is_pleo[0].video_id}
                            thumbnail={data.what_is_pleo[0].video_thumbnail}
                        />
                    </Section>
                )}

                {data.feature_blocks.map((block, i) => (
                    <Section key={`${block.title}-${i}`}>
                        <FeatureBlock
                            image={block.image}
                            title={block.title}
                            body={block.body.richText}
                            mirrored={!(i % 2)}
                        />
                    </Section>
                ))}

                {!!data.comparison_table && (
                    <FeaturesComparisonTable
                        title={data.comparison_table_title}
                        content={data.comparison_table.document}
                    />
                )}

                {hasNonEmptyItems(data.customers) && (
                    <Section>
                        <FeatureBlock
                            image={data.customers[0].image}
                            title={data.customers[0].title}
                            body={data.customers[0].body.richText}
                        >
                            <Button
                                variant="outline"
                                as={PrismicLink}
                                {...data.customers[0].cta_link}
                            >
                                {data.customers[0].cta_text}
                            </Button>
                        </FeatureBlock>
                    </Section>
                )}

                {data.body.length > 0 && <BigListSection slice={data.body[0]} />}

                {data.social_proof_logos_extended_section && (
                    <TitleWithIconGridSection
                        titleVariant="heading-large"
                        highlightColor="yellow"
                        title={
                            data.social_proof_logos_extended_section.document.data.title.richText
                        }
                        items={data.social_proof_logos_extended_section.document.data.logos.map(
                            (item) => ({
                                icon: item.logo,
                            })
                        )}
                    />
                )}

                {data.social_proof_ratings_section && (
                    <TitleWithIconGridSection
                        titleVariant="heading-large"
                        highlightColor="pink"
                        title={data.social_proof_ratings_section.document.data.title.richText}
                        items={data.social_proof_ratings_section.document.data.ratings.map(
                            (item) => ({
                                icon: item.rating.document.data.logo,
                                stars: item.rating.document.data.rating_number,
                                link: item.rating.document.data.link,
                            })
                        )}
                    />
                )}

                {data.signup_banner && (
                    <SignupBannerSection
                        {...data.signup_banner.document.data}
                        formId={formId}
                        color="pink"
                        placeholder={data.signup_banner.document.data.email_input_placeholder}
                        submitText={data.signup_banner.document.data.submit_text}
                        errorText={data.signup_banner.document.data.email_input_error_text}
                    />
                )}
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<WhyPleoQuery>) => {
    const pageData = data.prismicWhyPleoPage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

const VideoTitle = styled(TitleLayout)`
    margin-bottom: ${tokens.spacing20};

    ${mq.min('sm')} {
        margin-bottom: ${tokens.spacing40};
    }
`

const HowToHeader = styled.div`
    display: grid;
    gap: ${tokens.spacing20};
`

export const query = graphql`
    query WhyPleo($locale: String!, $imageBreakpoints: [Int], $twoColumnsAsymmetric: String) {
        prismicWhyPleoPage(lang: {eq: $locale}) {
            _previewable
            data {
                body {
                    ...BigListSlice
                }
                meta_title
                meta_description
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                hero {
                    title
                    body {
                        richText
                    }
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                        gatsbyImageData(
                            breakpoints: $imageBreakpoints
                            sizes: $twoColumnsAsymmetric
                        )
                    }
                    proof_point_badges {
                        document {
                            ... on PrismicProofPointBadgesRepeatable {
                                data {
                                    proof_point_first {
                                        title
                                        subtitle
                                        icon {
                                            alt
                                            copyright
                                            url
                                            dimensions {
                                                height
                                                width
                                            }
                                        }
                                    }
                                    proof_point_second {
                                        title
                                        subtitle
                                        icon {
                                            alt
                                            copyright
                                            url
                                            dimensions {
                                                height
                                                width
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                how_to_title
                how_to_subtitle {
                    richText
                }
                how_to_steps {
                    title
                }
                what_is_pleo {
                    video_thumbnail {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                        gatsbyImageData(
                            breakpoints: $imageBreakpoints
                            sizes: $twoColumnsAsymmetric
                        )
                    }
                    title
                    video_id
                }
                feature_blocks {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                        gatsbyImageData(
                            breakpoints: $imageBreakpoints
                            sizes: $twoColumnsAsymmetric
                        )
                    }
                    title
                    body {
                        richText
                    }
                }
                comparison_table_title
                comparison_table {
                    document {
                        ... on PrismicComparisonTable {
                            ...FeaturesComparisonTable
                        }
                    }
                }
                customers {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    body {
                        richText
                    }
                    cta_text
                    cta_link {
                        ...PrismicLink
                    }
                }
                social_proof_logos_extended_section {
                    document {
                        ... on PrismicSocialProofLogosExtendedSection {
                            data {
                                title {
                                    richText
                                }
                                logos {
                                    logo {
                                        alt
                                        copyright
                                        url
                                        dimensions {
                                            height
                                            width
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                social_proof_ratings_section {
                    document {
                        ... on PrismicSocialProofRatingsSection {
                            data {
                                title {
                                    richText
                                }
                                ratings {
                                    rating {
                                        document {
                                            ... on PrismicRating {
                                                data {
                                                    logo {
                                                        alt
                                                        copyright
                                                        url
                                                        dimensions {
                                                            height
                                                            width
                                                        }
                                                    }
                                                    stars
                                                    link
                                                    rating_number
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                signup_banner {
                    document {
                        ...SignupBanner
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(WhyPleo)
